/** Contas: gastáveis, reservas, cartões (com desenho de cartão) e crediários.
 * Cada grupo responde uma pergunta diferente, então nenhum deles tem a mesma cara.
 */

import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { ChipIcon, Icon } from "../components/Icon";
import {
  AccountTag,
  ErrorBanner,
  Face,
  Field,
  Gauge,
  Key,
  Label,
  Skeleton,
} from "../components/ui";
import { dayMonth, hojeLocal, money, parseMoney, sumMoney } from "../lib/format";
import { useAccounts, usePayStatement, usePlans, useStatements } from "../lib/queries";
import type { Account, AccountKind, InstallmentPlan, Statement } from "../lib/types";
import { NovaConta } from "./Ajustes";

/** Um grupo de contas. Aparece SEMPRE, mesmo vazio: se o grupo desaparecesse quando não há
 * nada dentro, não haveria onde clicar para criar o primeiro — foi o que aconteceu com os
 * cartões, que só existiam em Ajustes. */
function Grupo({
  titulo,
  nota,
  vazio,
  novo,
  onNovo,
  children,
}: {
  titulo: string;
  nota: string;
  vazio: boolean;
  novo: string;
  onNovo: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="group">
      <h3>
        {titulo} <small>{nota}</small>
        <Key onClick={onNovo}>{novo}</Key>
      </h3>
      {children}
      {vazio && (
        <button type="button" className="add" onClick={onNovo}>
          <Icon name="plus" />
          {novo}
        </button>
      )}
    </div>
  );
}

export function Contas() {
  const { data: accounts, isPending, error, refetch } = useAccounts();
  const { data: statements } = useStatements();
  const {
    data: plans,
    isPending: planosCarregando,
    error: planosErro,
    refetch: recarregarPlanos,
  } = usePlans();
  const pay = usePayStatement();
  const [pagando, setPagando] = useState<Statement | null>(null);
  const [criando, setCriando] = useState<AccountKind | null>(null);

  const grupos = useMemo(() => {
    const a = accounts ?? [];
    return {
      gastaveis: a.filter((x) => x.in_cashflow),
      reservas: a.filter((x) => !x.in_cashflow && (x.kind === "savings" || x.kind === "investment")),
      cartoes: a.filter((x) => x.kind === "credit_card"),
      crediarios: a.filter((x) => x.kind === "loan"),
    };
  }, [accounts]);

  if (isPending) return <section className="view on"><Skeleton lines={6} /></section>;
  if (error)
    return (
      <section className="view on">
        <ErrorBanner message={(error as Error).message} onRetry={() => refetch()} />
      </section>
    );

  const liquidez = sumMoney(grupos.gastaveis.map((a) => a.balance));
  const reservas = sumMoney(grupos.reservas.map((a) => a.balance));
  const faturasAbertas = sumMoney(
    (statements ?? []).filter((s) => s.status !== "paid").map((s) => s.amount_current),
  );
  const limiteTotal = sumMoney(grupos.cartoes.map((c) => c.credit_limit ?? 0));
  // "Crediários" é o saldo devedor de quem NÃO é cartão: uma compra parcelada no cartão
  // já está contada no limite usado logo acima, e somá-la aqui cobrava a mesma dívida duas
  // vezes — com 12× de R$ 500 o app mostrava R$ 6.000 no crédito e mais R$ 6.000 aqui.
  const idsCrediario = new Set(grupos.crediarios.map((c) => c.id));
  const planosCrediario = (plans ?? []).filter((p) => idsCrediario.has(p.account_id));
  const devedor = sumMoney(planosCrediario.map((p) => p.remaining ?? 0));

  const statementsDoCartao = (cardId: string) =>
    (statements ?? []).filter((s) => s.account_id === cardId);

  return (
    <section className="view on" aria-label="Contas">
      <div className="contas">
        <div className="strip">
          <div>
            <Label>Liquidez</Label>
            <span className="v">{money(liquidez)}</span>
            <span className="rule">no fluxo</span>
          </div>
          <div>
            <Label>Reservas</Label>
            <span className="v">{money(reservas)}</span>
            <span className="rule">fora do fluxo</span>
          </div>
          <div>
            <Label>Crédito disponível</Label>
            <span className="v">{money(Math.max(0, limiteTotal - faturasAbertas))}</span>
            <span className="rule">de {money(limiteTotal)}</span>
          </div>
          <div>
            <Label>Crediários</Label>
            <span className="v t-crit">{money(devedor)}</span>
            <span className="rule">saldo devedor</span>
          </div>
        </div>

        <Grupo
          titulo="Gastáveis"
          nota="entram no saldo"
          vazio={grupos.gastaveis.length === 0}
          novo="Nova conta"
          onNovo={() => setCriando("checking")}
        >
          <div className="plates">
            {grupos.gastaveis.map((a) => (
              <Placa key={a.id} conta={a} />
            ))}
          </div>
        </Grupo>

        <Grupo
          titulo="Reservas"
          nota="fora do fluxo"
          vazio={grupos.reservas.length === 0}
          novo="Nova reserva"
          onNovo={() => setCriando("savings")}
        >
          <div className="plates">
            {grupos.reservas.map((a) => (
              <Placa key={a.id} conta={a} />
            ))}
          </div>
        </Grupo>

        <Grupo
          titulo="Cartões"
          nota="saem no vencimento da fatura"
          vazio={grupos.cartoes.length === 0}
          novo="Novo cartão"
          onNovo={() => setCriando("credit_card")}
        >
          <div className="cards">
            {grupos.cartoes.map((c) => (
              <Cartao
                key={c.id}
                cartao={c}
                faturas={statementsDoCartao(c.id)}
                liquidacao={(accounts ?? []).find((a) => a.id === c.settlement_account_id)}
                onPagar={setPagando}
              />
            ))}
          </div>
        </Grupo>

        <Grupo
          titulo="Crediários"
          nota="dívida com fim"
          vazio={grupos.crediarios.length === 0}
          novo="Novo crediário"
          onNovo={() => setCriando("loan")}
        >
          {planosErro && (
            <ErrorBanner
              message={(planosErro as Error).message}
              onRetry={() => recarregarPlanos()}
            />
          )}
          <div className="plates">
            {grupos.crediarios.map((c) => (
              <Crediario
                key={c.id}
                conta={c}
                planos={planosCrediario.filter((p) => p.account_id === c.id)}
                liquidacao={(accounts ?? []).find((a) => a.id === c.settlement_account_id)}
                carregando={planosCarregando}
              />
            ))}
          </div>
        </Grupo>

        {/* quem arquivou uma conta procura por ela aqui, não em Ajustes */}
        <Link className="add" to="/contas/arquivadas">
          <Icon name="cards" />
          Contas arquivadas
        </Link>
      </div>

      {criando && <NovaConta inicial={criando} onClose={() => setCriando(null)} />}

      {pagando && (
        <PagarFatura
          fatura={pagando}
          contas={grupos.gastaveis}
          onClose={() => setPagando(null)}
          onPagar={(body) => {
            pay.mutate({ id: pagando.id, ...body });
            setPagando(null);
          }}
        />
      )}
    </section>
  );
}

function Placa({ conta }: { conta: Account }) {
  const sub =
    conta.kind === "checking"
      ? "conta corrente"
      : conta.kind === "cash"
        ? "em espécie"
        : conta.kind === "savings"
          ? "reserva"
          : conta.kind === "investment"
            ? "investimento"
            : "";
  // A placa inteira abre a conta (`/contas/conta/:id`): extrato, curva do saldo e edição.
  return (
    <Link
      to={`/contas/conta/${conta.id}`}
      aria-label={`Abrir ${conta.name}`}
      style={{ display: "block", textDecoration: "none", color: "inherit" }}
    >
      <Face className="plate">
        <span className="tagp" style={{ background: conta.color ?? "var(--ink-2)" }}>
          {conta.name.slice(0, 1)}
        </span>
        <div className="n">
          <b>
            {conta.name}{" "}
            {conta.shares && conta.shares.length > 0 && <span className="tag">compartilhada</span>}
          </b>
          <small>{sub}</small>
        </div>
        <div className="v">
          {money(conta.balance)}
          <small>saldo real</small>
        </div>
      </Face>
    </Link>
  );
}

/** Um crediário: a CONTA primeiro, os parcelamentos dela logo abaixo.
 *
 * A seção listava os PLANOS de parcela e não as contas. Crediário criado sem plano — que é o
 * caminho normal, porque o formulário nem perguntava a dívida — ficava invisível e
 * inalcançável para sempre: não dava para abrir, editar, arquivar nem excluir. Agora a conta
 * é a linha clicável (como o cartão) e o plano é detalhe dela, não um item solto.
 */
function Crediario({
  conta,
  planos,
  liquidacao,
  carregando,
}: {
  conta: Account;
  planos: InstallmentPlan[];
  liquidacao?: Account;
  carregando: boolean;
}) {
  const devedor = sumMoney(planos.map((p) => p.remaining ?? 0));

  return (
    <div style={{ display: "grid", gap: 4 }}>
      {/* A placa inteira abre `/contas/conta/:id` — a tela que edita, arquiva e exclui. */}
      <Link
        to={`/contas/conta/${conta.id}`}
        aria-label={`Abrir ${conta.name}`}
        style={{ display: "block", textDecoration: "none", color: "inherit" }}
      >
        <Face className="plate">
          <span className="tagp" style={{ background: conta.color ?? "var(--ink-2)" }}>
            {conta.name.slice(0, 1)}
          </span>
          <div className="n">
            <b>
              {conta.name}{" "}
              {conta.shares && conta.shares.length > 0 && <span className="tag">compartilhada</span>}
            </b>
            <small>
              {carregando
                ? "lendo os parcelamentos…"
                : planos.length === 0
                  ? "nenhuma dívida declarada"
                  : `${planos.length} ${planos.length === 1 ? "parcelamento" : "parcelamentos"}`}
              {liquidacao ? ` · parcela sai de ${liquidacao.name}` : " · sem conta de liquidação"}
            </small>
          </div>
          <div className="v">
            {carregando ? "—" : money(devedor)}
            <small>saldo devedor</small>
          </div>
        </Face>
      </Link>

      {/* O motor passou a transferir cada parcela da conta de liquidação para o crediário no
          vencimento. Sem essa conta o crediário guarda a dívida e não move dinheiro nenhum —
          é melhor ler isso aqui do que descobrir no fim do mês. */}
      {!liquidacao && (
        <div className="rule" style={{ padding: "0 12px" }}>
          Sem conta de liquidação as parcelas não saem de caixa nenhum. Abra o crediário e diga
          de onde o dinheiro sai.
        </div>
      )}

      {carregando && <Skeleton lines={1} />}

      {!carregando && planos.length > 0 && (
        <Face className="log">
          {planos.map((p) => {
            const pagas = p.paid_count ?? 0;
            return (
              <div className="line p-out" key={p.id}>
                <div className="d">
                  parcela
                  <b>
                    {Math.min(pagas + 1, p.installment_count)}/{p.installment_count}
                  </b>
                </div>
                <div className="what">
                  <div className="t">
                    <span className="desc">{p.description}</span>
                  </div>
                  <small>
                    {pagas} de {p.installment_count} pagas
                    {p.payee ? ` · ${p.payee}` : ""}
                    {p.next_on ? ` · próxima ${dayMonth(p.next_on)}` : ""}
                  </small>
                  <Gauge pct={(pagas / p.installment_count) * 100} />
                </div>
                <div className="amt">
                  <span>{money(p.remaining ?? 0)}</span>
                  <small>restam {p.installment_count - pagas}×</small>
                </div>
              </div>
            );
          })}
        </Face>
      )}
    </div>
  );
}

function Cartao({
  cartao,
  faturas,
  liquidacao,
  onPagar,
}: {
  liquidacao?: Account;
  cartao: Account;
  faturas: Statement[];
  onPagar: (s: Statement) => void;
}) {
  const nav = useNavigate();
  // A fatura que interessa é a próxima a vencer entre as não pagas (a vencida vem antes de
  // tudo). Pegar "a primeira aberta" trazia uma fatura de 2027 com R$ 0,00.
  const aberta =
    [...faturas]
      .filter((f) => f.status !== "paid")
      .sort((a, b) => a.due_on.localeCompare(b.due_on))[0] ?? faturas[0];
  // Limite usado é TUDO que está em fatura não paga, não só a próxima a vencer. Havia três
  // fórmulas diferentes para o mesmo número (a tira do topo, esta placa e a tela do
  // cartão); com qualquer compra parcelada elas divergiam, porque o motor cria uma fatura
  // por mês futuro. Agora é uma só, e é a que a tira usa.
  const usado = sumMoney(faturas.filter((f) => f.status !== "paid").map((f) => f.amount_current));
  const limite = cartao.credit_limit ?? 0;

  return (
    <div className="ccard">
      <div
        className="pvc"
        style={
          {
            "--c1": cartao.color ?? "#2A2E31",
            "--c2": "#17191B",
          } as React.CSSProperties
        }
      >
        <div className="top">
          <span className="bank">{cartao.name}</span>
          {/* a classe importa: `.pvc .wave` é quem dá 20px ao contactless. Sem ela o SVG
              não tem tamanho e cresce até ocupar o cartão inteiro. */}
          <Icon name="wave" className="wave" />
        </div>
        <div className="mid">
          <ChipIcon />
          <span className="number">•••• •••• •••• ••••</span>
        </div>
        <div className="bot">
          <span className="holder">{cartao.owner_name ?? ""}</span>
          <span className="meta">
            fecha dia {cartao.closing_day}
            <br />
            vence dia {cartao.due_day}
          </span>
        </div>
      </div>
      <Face className="cplate">
        {aberta ? (
          <>
            <div className="r">
              <Label>
                Fatura {aberta.period} · {aberta.status === "open" ? "aberta" : aberta.status === "closed" ? "fechada" : "paga"}
              </Label>
              <Label dim>
                fecha {dayMonth(aberta.closing_on)} · vence {dayMonth(aberta.due_on)}
              </Label>
            </div>
            <div className="r">
              <span className="v">{money(usado)}</span>
              <span className="k">
                <Key onClick={() => nav(`/contas/cartao/${cartao.id}`)}>Detalhes</Key>
                {aberta.status !== "paid" && <Key onClick={() => onPagar(aberta)}>Pagar</Key>}
              </span>
            </div>
          </>
        ) : (
          <div className="r">
            <Label>Sem fatura ainda</Label>
            <Key onClick={() => nav(`/contas/cartao/${cartao.id}`)}>Detalhes</Key>
          </div>
        )}
        {limite > 0 && (
          <div>
            <div className="r">
              <span className="rule">limite disponível</span>
              <span className="rule num">
                {money(Math.max(0, limite - usado))} de {money(limite)}
              </span>
            </div>
            <Gauge pct={(usado / limite) * 100} />
          </div>
        )}
        {liquidacao && (
          <div className="rule">
            paga por <AccountTag name={liquidacao.name} color={liquidacao.color} />
          </div>
        )}
      </Face>
    </div>
  );
}

/** Exportada porque a tela do cartão (`/contas/cartao/:id`) paga a mesma fatura: dois
 * formulários de pagamento seriam duas regras de arredondamento para divergir. */
export function PagarFatura({
  fatura,
  contas,
  onClose,
  onPagar,
}: {
  fatura: Statement;
  contas: Account[];
  onClose: () => void;
  onPagar: (body: { amount?: number; paid_on?: string; from_account_id?: string }) => void;
}) {
  const hoje = hojeLocal();
  const [valor, setValor] = useState(fatura.amount_current.toFixed(2).replace(".", ","));
  const [data, setData] = useState(hoje);
  const [conta, setConta] = useState(contas[0]?.id ?? "");
  const amount = parseMoney(valor);

  return (
    <div className="sheet on" role="dialog" aria-modal="true" aria-label="Pagar fatura">
      <div className="panel">
        <div className="handle" />
        <h2>
          <span>
            Pagar fatura {fatura.account_name} {fatura.period}
          </span>
          <Key icon="x" title="Fechar" onClick={onClose} />
        </h2>
        <Field label="Valor pago" className="amount">
          <input inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} />
        </Field>
        <Field label="Quando">
          <input type="date" value={data} onChange={(e) => setData(e.target.value)} />
        </Field>
        <Field label="De qual conta">
          <select value={conta} onChange={(e) => setConta(e.target.value)}>
            {contas.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} · {money(c.balance)}
              </option>
            ))}
          </select>
        </Field>
        <div className="acts">
          <Key onClick={onClose}>Cancelar</Key>
          <Key
            primary
            disabled={!(amount > 0) || !conta}
            onClick={() => onPagar({ amount, paid_on: data, from_account_id: conta })}
          >
            Pagar {money(amount)}
          </Key>
        </div>
      </div>
    </div>
  );
}
